import { useEffect, useRef, useState } from 'react'
import { Bell, BellOff, ChevronRight, Flag, FolderPlus, KeyRound, LogOut, Plus, Settings, Trash2, UserPlus, UserRound, Zap } from 'lucide-react'
import { useAppStore } from '@/store/app'
import { useAuthStore } from '@/store/auth'
import { guildMuted, useGuildStore } from '@/store/guilds'
import { P, has } from '@/lib/permissions'
import { ChannelType, type Guild } from '@/types/api'
import { cx } from '@/lib/cx'
import { InviteModal } from './InviteModal'
import { ConfirmModal } from './ConfirmModal'
import { ReportModal } from '../ReportModal'
import { ClaimModal } from './ClaimModal'
import { MemberProfileModal } from './MemberProfileModal'
import styles from './GuildMenu.module.css'

interface Props {
  guild: Guild
  perms: bigint
  onClose: () => void
  onCreateChannel: (type: number) => void
}

/** Выпадающее меню сервера под шапкой колонки каналов. */
export function GuildMenu({ guild, perms, onClose, onCreateChannel }: Props) {
  const me = useAuthStore((s) => s.user)
  const openGuildSettings = useAppStore((s) => s.openGuildSettings)
  const setView = useAppStore((s) => s.setView)
  const leaveGuild = useGuildStore((s) => s.leaveGuild)
  const channels = useGuildStore((s) => s.channels[guild.id])
  const settings = useGuildStore((s) => s.settings[guild.id])
  const updateSettings = useGuildStore((s) => s.updateSettings)
  const [invite, setInvite] = useState(false)
  const [claiming, setClaiming] = useState(false)
  const [leaving, setLeaving] = useState(false)
  const [reporting, setReporting] = useState(false)
  const [profile, setProfile] = useState(false)
  const [notify, setNotify] = useState(false)
  const muted = guildMuted(settings)
  const level = settings?.message_notifications ?? 3
  const ref = useRef<HTMLDivElement | null>(null)
  const owner = me?.id === guild.owner_id
  const canInvite = has(perms, P.CREATE_INSTANT_INVITE)
  const canSettings = has(perms, P.MANAGE_GUILD) || has(perms, P.MANAGE_ROLES) || has(perms, P.BAN_MEMBERS) || has(perms, P.KICK_MEMBERS)
  const canChannels = has(perms, P.MANAGE_CHANNELS)
  const firstText = Object.values(channels ?? {}).find((c) => c.type === ChannelType.GuildText)

  // пока открыт любой диалог, меню не рендерится — и клики по диалогу не должны его закрывать:
  // иначе исчезает и сам диалог, ведь он живёт внутри меню. «Профиль на сервере» раньше в этот
  // список не входил, и нажатие на «Выбрать картинку» закрывало окно раньше, чем открывался выбор файла
  const dialogOpen = invite || leaving || reporting || claiming || profile
  useEffect(() => {
    if (dialogOpen) return
    const onClick = (e: MouseEvent) => {
      if (!ref.current || ref.current.contains(e.target as Node)) return
      onClose()
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [onClose, dialogOpen])

  if (reporting) {
    return <ReportModal target={{ type: 'guild', id: guild.id, label: `сервер «${guild.name}»` }} onClose={() => { setReporting(false); onClose() }} />
  }
  if (claiming) return <ClaimModal guildId={guild.id} onClose={() => { setClaiming(false); onClose() }} />
  if (profile) return <MemberProfileModal guildId={guild.id} guildName={guild.name} onClose={() => { setProfile(false); onClose() }} />
  if (invite && firstText) {
    return <InviteModal guild={guild} channelId={firstText.id} onClose={() => { setInvite(false); onClose() }} />
  }
  if (leaving) {
    return (
      <ConfirmModal
        title={`Покинуть «${guild.name}»`}
        text="Вы уверены, что хотите покинуть сервер? Вернуться можно будет только по новому приглашению."
        confirmLabel="Покинуть сервер"
        onCancel={() => { setLeaving(false); onClose() }}
        onConfirm={async () => {
          await leaveGuild(guild.id)
          setView({ kind: 'home', page: 'friends' })
          onClose()
        }}
      />
    )
  }

  return (
    <div className={cx(styles.menu, 'klichat-popover')} role="menu" ref={ref}>
      {canInvite && firstText ? (
        <button type="button" role="menuitem" className={cx(styles.item, styles.accent)} onClick={() => setInvite(true)}>
          Пригласить людей <UserPlus size={18} />
        </button>
      ) : null}
      {canSettings ? (
        <button type="button" role="menuitem" className={styles.item} onClick={() => { openGuildSettings(guild.id); onClose() }}>
          Настройки сервера <Settings size={18} />
        </button>
      ) : null}
      {canChannels ? (
        <>
          <button type="button" role="menuitem" className={styles.item} onClick={() => { onCreateChannel(ChannelType.GuildText); onClose() }}>
            Создать канал <Plus size={18} />
          </button>
          <button type="button" role="menuitem" className={styles.item} onClick={() => { onCreateChannel(ChannelType.GuildCategory); onClose() }}>
            Создать категорию <FolderPlus size={18} />
          </button>
        </>
      ) : null}
      <button type="button" role="menuitem" className={styles.item} onClick={() => setProfile(true)}>
        Профиль на сервере <UserRound size={18} />
      </button>
      {/* до вкладки «Буст» иначе не добраться: она внутри настроек сервера, а их видят
          только те, кто может ими управлять */}
      <button type="button" role="menuitem" className={styles.item} onClick={() => { openGuildSettings(guild.id, 'boost'); onClose() }}>
        Усилить сервер <Zap size={18} />
      </button>
      <button type="button" role="menuitem" className={styles.item} aria-expanded={notify} onClick={() => setNotify((v) => !v)}>
        Уведомления <ChevronRight size={18} className={cx(styles.chevron, notify && styles.chevronOpen)} />
      </button>
      {notify ? (
        <div className={styles.submenu}>
          <button type="button" role="menuitemcheckbox" aria-checked={muted} className={styles.item} onClick={() => void updateSettings(guild.id, { muted: !muted })}>
            {muted ? 'Включить сервер' : 'Заглушить сервер'} {muted ? <Bell size={16} /> : <BellOff size={16} />}
          </button>
          {([
            [0, 'Все сообщения'],
            [1, 'Только @упоминания'],
            [2, 'Ничего'],
          ] as const).map(([value, label]) => (
            <button key={value} type="button" role="menuitemradio" aria-checked={level === value} className={cx(styles.item, styles.radio, level === value && styles.radioOn)} onClick={() => void updateSettings(guild.id, { message_notifications: value })}>
              <span className={styles.dot} /> {label}
            </button>
          ))}
          <button type="button" role="menuitemcheckbox" aria-checked={Boolean(settings?.suppress_everyone)} className={cx(styles.item, styles.check, settings?.suppress_everyone && styles.checkOn)} onClick={() => void updateSettings(guild.id, { suppress_everyone: !settings?.suppress_everyone })}>
            <span className={styles.box} /> Не считать @everyone и @here
          </button>
        </div>
      ) : null}
      <button type="button" role="menuitem" className={styles.item} onClick={() => setClaiming(true)}>
        Код переноса из Discord <KeyRound size={18} />
      </button>
      <div className={styles.separator} role="separator" />
      {!owner ? (
        <button type="button" role="menuitem" className={styles.item} onClick={() => setReporting(true)}>
          Пожаловаться на сервер <Flag size={18} />
        </button>
      ) : null}
      {owner ? (
        <button type="button" role="menuitem" className={cx(styles.item, styles.danger)} onClick={() => { openGuildSettings(guild.id, 'overview'); onClose() }}>
          Удалить сервер <Trash2 size={18} />
        </button>
      ) : (
        <button type="button" role="menuitem" className={cx(styles.item, styles.danger)} onClick={() => setLeaving(true)}>
          Покинуть сервер <LogOut size={18} />
        </button>
      )}
    </div>
  )
}
